import { useEffect, useRef, useState } from 'react';
import type { Gif } from '@comms/shared';
import { api } from '../lib/api';
import { ErrorText, Spinner } from './ui';

/**
 * GIF search. Results and media come from our own server, which proxies Giphy, so your
 * browser never talks to Giphy directly.
 */
export function GifPicker({ onPick, onClose }: { onPick: (g: Gif) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<Gif[] | null>(null);
  const [more, setMore] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const loading = useRef(false);

  useEffect(() => {
    const t = window.setTimeout(() => window.addEventListener('click', onClose, { once: true }), 0);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('click', onClose);
    };
  }, [onClose]);

  // Debounce typing.
  useEffect(() => {
    const t = window.setTimeout(() => setQuery(q.trim()), 350);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let alive = true;
    setItems(null);
    setMore(true);
    api<Gif[]>('GET', `/gifs/search?q=${encodeURIComponent(query)}`)
      .then((r) => alive && (setItems(r), setMore(r.length > 0)))
      .catch((err: unknown) => alive && (setError(err), setItems([])));
    return () => {
      alive = false;
    };
  }, [query]);

  async function loadMore() {
    if (loading.current || !more || !items) return;
    loading.current = true;
    try {
      const next = await api<Gif[]>(
        'GET',
        `/gifs/search?q=${encodeURIComponent(query)}&offset=${items.length}`,
      );
      const seen = new Set(items.map((g) => g.id));
      setItems([...items, ...next.filter((g) => !seen.has(g.id))]);
      setMore(next.length > 0);
    } catch (err) {
      setError(err);
    } finally {
      loading.current = false;
    }
  }

  return (
    <div
      className="absolute bottom-10 right-0 z-30 flex h-96 w-96 flex-col rounded bg-bg-0 p-2 shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      <input
        autoFocus
        aria-label="Search GIFs"
        placeholder="Search GIFs"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="mb-2 rounded bg-bg-2 px-3 py-1.5 text-sm text-text-0 outline-none"
      />
      <div
        className="grid flex-1 grid-cols-2 content-start gap-1.5 overflow-y-auto"
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollTop + el.clientHeight > el.scrollHeight - 200) void loadMore();
        }}
      >
        {!items && <Spinner className="col-span-2 mx-auto mt-8" />}
        {items?.length === 0 && (
          <p className="col-span-2 mt-8 text-center text-sm text-text-3">No GIFs found.</p>
        )}
        {items?.map((g) => (
          <button
            key={g.id}
            type="button"
            title={g.title}
            aria-label={g.title || 'GIF'}
            onClick={() => onPick(g)}
            className="overflow-hidden rounded bg-bg-2 hover:ring-2 hover:ring-accent"
          >
            <video
              src={g.previewUrl}
              autoPlay
              loop
              muted
              playsInline
              className="h-28 w-full object-cover"
            />
          </button>
        ))}
      </div>
      <ErrorText error={error} />
      <div className="pt-1 text-right text-[10px] font-bold tracking-wide text-text-3">
        Powered by GIPHY
      </div>
    </div>
  );
}
